import React, { useContext, useEffect, useState } from 'react';
import { AsYouType, validatePhoneNumberLength } from 'libphonenumber-js';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import InputWrapper from '../../commons/components/input-wrapper';
import useApiPostOrderError from '../../hooks/useApiPostOrderError';
import useDidUpdateEffect from '../../hooks/useDidUpdateEffect';
import usePortalSettings from '../../hooks/usePortalSettings';
import useErrors from '../../store/errors/useErrors';
import DataContext from '../../store/dataContext';
import UiContext from '../../store/uiContext';
import { setStatus } from '../../modules/billing/slice';

import Newsletter from '../Newsletter';
import BillingPhoneWithDDI from '../BillingPhoneWithDDI';

const BillingPhone = ({ isBillingAddress, flags }) => {
  const {
    billingPhone,
    updateBillingPhone,
    contextBillingAddress,
    contextShippingAddress,
    resetTimestamp,
    setValidNumber,
    apiEmailResponse,
  } = useContext(DataContext);
  const uiContext = useContext(UiContext);
  const { portalSettings } = usePortalSettings();
  const [countryCode, setCountryCode] = useState('US');
  const [ddi, setDDI] = useState('');
  const { errors, addErrorToField, resetErrorOnField, hasErrorOnField, hasHighlightOnField } = useErrors();
  const { hasPhoneError } = useApiPostOrderError();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isDisabled = uiContext.checkoutStage === uiContext.TO_USER_ADD;
  const phoneLabel =
    portalSettings.contactPhone.isVisible && !isBillingAddress
      ? t('billing_phone.phone_number')
      : t('billing_phone.billing_phone');
  const isPhoneRequired = portalSettings.contactPhone.isRequired || hasPhoneError();

  const setPhoneNumberSuccess = () => {
    resetErrorOnField('billingPhone');
    dispatch(setStatus({ isReady: true }));
  };

  const setPhoneNumberError = message => {
    addErrorToField({
      billingPhone: {
        hasHighlight: false,
        message,
      },
    });
  };

  const validatePhone = (phone, country) => {
    const clearPhone = phone?.replace(/\D/g, '');
    const noDDIPhone = clearPhone?.substring(ddi?.length);
    if (country === 'US') {
      if (clearPhone?.indexOf('11') === 0) {
        return 'NOT_A_NUMBER';
      }
      return validatePhoneNumberLength(phone, country);
    }
    if (country === 'ZA' && noDDIPhone?.length < 8) {
      return 'TOO_SHORT';
    }
    return validatePhoneNumberLength(noDDIPhone, country);
  };

  const validatePhoneNumber = () => {
    if (portalSettings.contactPhone.isRequired && billingPhone === '') {
      setPhoneNumberError(`${t('billing_phone.please_enter_a_valid')} ${phoneLabel}`);
      return;
      // eslint-disable-next-line no-else-return
    } else if (!portalSettings.contactPhone.isRequired && billingPhone === '' && !hasPhoneError()) {
      setPhoneNumberSuccess();
      return;
    }

    const validate = validatePhone(billingPhone, countryCode);
    let message;
    if (validate === 'TOO_SHORT') {
      message = `The ${phoneLabel} is too short`;
    } else if (validate === 'TOO_LONG') {
      message = `The ${phoneLabel} is too long`;
    } else if (validate === 'NOT_A_NUMBER') {
      message = `Please enter a valid ${phoneLabel}`;
    }
    if (message) {
      setValidNumber(false);
      setPhoneNumberError(message);
    } else {
      setPhoneNumberSuccess();
      setValidNumber(true);
    }
  };

  const saveInfoInput = info => {
    if (info.length > 16) {
      return;
    }
    if (info.length >= (5 || info.length)) {
      updateBillingPhone(new AsYouType(countryCode).input(info));
    } else {
      updateBillingPhone(info);
    }
  };

  const resetState = () => {
    updateBillingPhone('');
    resetErrorOnField('billingPhone');
    setCountryCode('US');
  };

  const handleFocus = () => {
    resetErrorOnField('billingPhone');
    uiContext.updateLocalError(
      uiContext.localError.nameError,
      uiContext.localError.addressError,
      uiContext.localError.creditCardError,
    );
  };

  const handleChange = event => {
    saveInfoInput(event.target.value);
  };

  const getError = () => {
    if (hasErrorOnField('billingPhone')) {
      return errors.billingPhone.message;
    }
    return '';
  };

  useEffect(() => {
    if (contextBillingAddress.countryIso2) {
      setCountryCode(contextBillingAddress.countryIso2);
    } else if (contextShippingAddress.countryIso2) {
      setCountryCode(contextShippingAddress.countryIso2);
    } else {
      setCountryCode('US');
    }
  }, [contextBillingAddress?.countryIso2, contextShippingAddress?.countryIso2]);

  useEffect(() => {
    const waitForApiEmailResponse = () => {
      const hasApiEmailResponse = apiEmailResponse && Object.values(apiEmailResponse).length > 0;
      if (hasApiEmailResponse && apiEmailResponse?.success && apiEmailResponse?.suggestions?.autofill?.billPhone) {
        dispatch(setStatus({ isReady: true }));
      }
    };
    waitForApiEmailResponse();
  }, [apiEmailResponse]);

  useDidUpdateEffect(resetState, resetTimestamp);

  return (
    <div className="container-phone">
      {!flags?.['disable-ddi-phone'] ? (
        <BillingPhoneWithDDI
          validatePhoneNumber={validatePhoneNumber}
          classNameInput={classNames({
            'is-value': billingPhone,
            'has-error': hasErrorOnField('billingPhone'),
            'has-highlight': hasHighlightOnField('billingPhone'),
          })}
          code={ddi}
          setCode={setDDI}
          country={countryCode}
          setCountry={setCountryCode}
          disabled={isDisabled}
          error={getError()}
          onChange={handleChange}
          onFocus={handleFocus}
          placeholder={`${isPhoneRequired ? '*' : ''} ${phoneLabel}`}
          placeholderTop={phoneLabel}
        />
      ) : (
        <InputWrapper
          classNameInput={classNames({
            'is-value': billingPhone,
            'has-error': hasErrorOnField('billingPhone'),
            'has-highlight': hasHighlightOnField('billingPhone'),
          })}
          disabled={isDisabled}
          error={getError()}
          inputId="phone"
          inputMode="tel"
          onBlur={validatePhoneNumber}
          onChange={handleChange}
          onFocus={handleFocus}
          placeholder={`${isPhoneRequired ? '*' : ''} ${phoneLabel}`}
          placeholderTop={phoneLabel}
          theme={portalSettings.theme}
          type="tel"
          value={billingPhone}
        />
      )}
      {portalSettings?.newsletter?.consent?.hasSms && <Newsletter />}
    </div>
  );
};

BillingPhone.propTypes = {
  isBillingAddress: PropTypes.bool,
  flags: PropTypes.shape({
    'disable-ddi-phone': PropTypes.bool,
  }),
};

export default BillingPhone;
